import React from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  Phone,
  MapPin,
  Users,
  Heart,
  Download,
  Droplets,
  Utensils,
  GraduationCap,
  Stethoscope,
  UserCheck
} from 'lucide-react';
import { CONTACT_INFO, ASHUD_INFO, formatAddress } from '../utils/constants';

function Footer() {
  return (
    <footer className="bg-white text-body border-t border-line">
      <div className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Logo and Description */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-5">
                <div>
                  <h3 className="text-lg font-bold text-ink">{ASHUD_INFO.name}</h3>
                  <p className="text-muted text-sm font-medium">{ASHUD_INFO.motto}</p>
                </div>
              </div>
              <p className="text-body leading-relaxed mb-6 max-w-md">
                {ASHUD_INFO.fullName}, œuvrant pour
                l'amélioration des conditions de vie des populations vulnérables au Tchad.
              </p>
              <div className="flex items-center gap-5">
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="text-muted hover:text-primary transition-colors duration-200"
                  aria-label="Email"
                >
                  <Mail className="w-5 h-5" />
                </a>
                <a
                  href={`tel:${CONTACT_INFO.phone}`}
                  className="text-muted hover:text-primary transition-colors duration-200"
                  aria-label="Téléphone"
                >
                  <Phone className="w-5 h-5" />
                </a>
                <a
                  href="https://maps.google.com/?q=Moundou,+Tchad"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted hover:text-primary transition-colors duration-200"
                  aria-label="Voir notre localisation sur Google Maps"
                >
                  <MapPin className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-ink text-sm font-semibold uppercase tracking-wide mb-4">
                Liens rapides
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/" className="text-body hover:text-primary transition-colors duration-200">
                    Accueil
                  </Link>
                </li>
                <li>
                  <Link to="/a-propos" className="text-body hover:text-primary transition-colors duration-200">
                    À propos
                  </Link>
                </li>
                <li>
                  <Link to="/projets" className="text-body hover:text-primary transition-colors duration-200">
                    Nos projets
                  </Link>
                </li>
                <li>
                  <Link to="/devenir-membre" className="text-body hover:text-primary transition-colors duration-200">
                    Devenir membre
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-body hover:text-primary transition-colors duration-200">
                    Contact
                  </Link>
                </li>
                <li>
                  <Link to="/mentions-legales" className="text-body hover:text-primary transition-colors duration-200">
                    Mentions légales
                  </Link>
                </li>
              </ul>
            </div>

            {/* Our Actions */}
            <div>
              <h4 className="text-ink text-sm font-semibold uppercase tracking-wide mb-4">
                Nos actions
              </h4>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-body">
                  <Droplets className="w-4 h-4 text-primary shrink-0" />
                  <span>Accès à l'eau potable</span>
                </li>
                <li className="flex items-center gap-3 text-body">
                  <Utensils className="w-4 h-4 text-primary shrink-0" />
                  <span>Assistance alimentaire</span>
                </li>
                <li className="flex items-center gap-3 text-body">
                  <GraduationCap className="w-4 h-4 text-primary shrink-0" />
                  <span>Parrainage scolaire</span>
                </li>
                <li className="flex items-center gap-3 text-body">
                  <Stethoscope className="w-4 h-4 text-primary shrink-0" />
                  <span>Santé communautaire</span>
                </li>
                <li className="flex items-center gap-3 text-body">
                  <UserCheck className="w-4 h-4 text-primary shrink-0" />
                  <span>Formation des femmes</span>
                </li>
              </ul>

              <a
                href="/assets/Livret-Accueil-ASHUD-Tchad.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full text-primary border border-primary hover:bg-primary-light font-semibold py-2.5 px-4 rounded transition-colors duration-200"
                aria-label="Télécharger le livret d'accueil ASHUD Tchad en PDF"
              >
                <Download className="w-4 h-4" />
                <span>Télécharger notre livret</span>
              </a>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="mt-12 pt-8 border-t border-line">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              <div className="text-center lg:text-left">
                <p className="text-body font-medium mb-1">
                  &copy; 2024 {ASHUD_INFO.name}. Tous droits réservés.
                </p>
                <p className="text-muted text-sm">
                  {ASHUD_INFO.fullName}
                </p>
                <p className="text-muted text-sm">
                  Siège social: {formatAddress(CONTACT_INFO.address)}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/devenir-membre"
                  className="bg-primary hover:bg-primary-dark text-white px-5 py-2.5 rounded font-semibold text-center transition-colors duration-200"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>Rejoignez-nous</span>
                  </span>
                </Link>
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="bg-white hover:bg-surface text-primary px-5 py-2.5 rounded font-semibold text-center border border-primary transition-colors duration-200"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Heart className="w-4 h-4" />
                    <span>Faire un don</span>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
